/**
 * Settings Service
 * API client for coach character configuration and voice preference management.
 */

import { apiClient } from './api';
import type { LanguageCode } from '@/config/languages';

// --- Types ---

export type PersonaId =
  | 'calm-anchor'
  | 'wise-guide'
  | 'thoughtful-listener'
  | 'practical-helper'
  | 'steady-companion'
  | 'clear-explainer';

/**
 * Languages a user can hold as their preference. Despite the name this drives
 * the whole language experience (agent prompts + staff-chat translation), not
 * just TTS voice. The set is declared once in `agents/shared/languages.json`
 * and mirrored by `@/config/languages` (#1745); text-only codes such as 'fil'
 * are valid stored preferences (staff-chat translation) that the companion
 * selector never offers (DECISIONS 2026-09-05, spring-clean Q8; #1741).
 */
export type VoiceLanguage = LanguageCode;

export type PersonaArchetype = 'warm' | 'grounded' | 'clear';

export interface CoachCharacterInfo {
  id: PersonaId;
  name: string;
  tagline: string;
  archetype: PersonaArchetype;
  gender: 'male' | 'female';
  languages: VoiceLanguage[];
}

export interface PersonaPreferences {
  persona: PersonaId;
  language: VoiceLanguage;
  accent: string;
  speedAdjustment: number;
}

export interface PersonaConfig {
  provider: string;
  voiceId: string;
  emotion: string;
  speed: number;
  personaId: string;
  personaName: string;
  personaTagline: string;
}

export interface PersonaPreferencesResponse extends PersonaPreferences {
  personaConfig: PersonaConfig;
}

export interface CoachCharactersResponse {
  archetypes: Record<string, { displayName: string; description: string }>;
  languages: Record<string, { name: string; flag: string; defaultAccent: string }>;
  accents: Record<string, Record<string, { name: string; flag: string }>>;
  characters: CoachCharacterInfo[];
}

export type PersonaPreferencesUpdate = Partial<Pick<PersonaPreferences, 'persona' | 'language' | 'accent' | 'speedAdjustment'>>;

/** Maya, the default persona for new parents (#3066). Mirrors the backend's
 *  DEFAULT_PERSONA in config/coachCharacters.js. */
export const DEFAULT_PERSONA_ID: PersonaId = 'practical-helper';

export function speedLabel(value: number): string {
  if (value <= -0.1) return 'Slower';
  if (value >= 0.1) return 'Faster';
  return 'Normal';
}

// --- API Functions ---

export async function getCoachCharacters(): Promise<CoachCharactersResponse> {
  const response = await apiClient.get<{ status: string; data: CoachCharactersResponse }>(
    '/settings/coach-characters'
  );
  return response.data.data;
}

export async function getPersonaPreferences(): Promise<PersonaPreferencesResponse> {
  const response = await apiClient.get<{ status: string; data: PersonaPreferencesResponse }>(
    '/settings/persona-preferences'
  );
  return response.data.data;
}

export async function updatePersonaPreferences(
  input: PersonaPreferencesUpdate
): Promise<PersonaPreferencesResponse> {
  const response = await apiClient.put<{ status: string; data: PersonaPreferencesResponse }>(
    '/settings/persona-preferences',
    input
  );
  return response.data.data;
}
